export type PostLockWarning = {
  step: string;
  message: string;
};

export type LockCycleDisplayInput = {
  id: number;
  lockedBy: number | null;
  lockedByName?: string | null;
  lockedByEmail?: string | null;
  lockedByDisplay?: string | null;
  lockReason?: string;
  version?: number | null;
  removalGenerationMode?: 'planned_only' | 'confirmed_plus_planned' | null;
  reassignmentId?: number | null;
  postLockWarnings?: PostLockWarning[];
};

function trimOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function resolveLockedByPrimary(lock: LockCycleDisplayInput): string {
  return (
    trimOrNull(lock.lockedByDisplay)
    ?? trimOrNull(lock.lockedByName)
    ?? trimOrNull(lock.lockedByEmail)
    ?? 'Unknown user'
  );
}

export function resolveLockedByOperational(lock: LockCycleDisplayInput): {
  primary: string;
  secondary: string | null;
} {
  const primary = resolveLockedByPrimary(lock);
  const email = trimOrNull(lock.lockedByEmail);
  const secondary =
    email && email.toLowerCase() !== primary.toLowerCase() ? email : null;
  return { primary, secondary };
}

export function formatRemovalMethodology(mode: string | null | undefined): {
  label: string;
  helperText: string | null;
} {
  switch (mode) {
    case 'confirmed_plus_planned':
      return {
        label: 'Confirmed + planned',
        helperText:
          'Uses approved evidence where available, with planned assignments as fallback.',
      };
    case 'planned_only':
      return {
        label: 'Planned only',
        helperText: 'Legacy mode. Uses planned assignments only.',
      };
    default:
      return { label: '—', helperText: null };
  }
}

export function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}

export function formatLockStatus(warnings: PostLockWarning[]): {
  label: string;
  hint: string | null;
  isHealthy: boolean;
} {
  if (warnings.length === 0) {
    return { label: 'Healthy', hint: null, isHealthy: true };
  }

  const firstMessage = warnings[0]?.message?.trim();
  return {
    label: `Needs attention (${warnings.length} warning${warnings.length === 1 ? '' : 's'})`,
    hint: firstMessage ? truncateText(firstMessage, 80) : null,
    isHealthy: false,
  };
}

export function buildLockedByDisplayFromUserFields(fields: {
  name?: string | null;
  email?: string | null;
  supabaseEmail?: string | null;
  username?: string | null;
}): string | null {
  return (
    trimOrNull(fields.name)
    ?? trimOrNull(fields.email)
    ?? trimOrNull(fields.supabaseEmail)
    ?? trimOrNull(fields.username)
  );
}
